import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Snackbar,
  Alert,
  InputAdornment,
  Divider,
  Chip,
  ListItemAvatar,
  Avatar,
  Fab,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  CardActions,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  PhoneIphone as MobileIcon,
  Email as EmailIcon,
  AccountCircle,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Visibility as ViewIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Lock as LockIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  DeleteSweep as DeleteSweepIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  FilterList as FilterListIcon,
  ContactPhone as ContactPhoneIcon,
  ContactMail as ContactMailIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { Contact } from '../types/Contact';
import ContactService from '../services/ContactService';
import authService from '../services/AuthService';
import { getApiUrl } from '../utils/env';
import { useAuth } from '../contexts/AuthContext';
import ContactDialog from './ContactDialog';
import AlphabeticScrollbar from './AlphabeticScrollbar';
import ImportDialog from './ImportDialog';
import DeleteAllContactsDialog from './DeleteAllContactsDialog';
import DeleteAgrarmonitorDialog from './DeleteAgrarmonitorDialog';
import AgrarmonitorSetupDialog from './AgrarmonitorSetupDialog';
import { formatPhoneNumberDIN5008 } from '../utils/phoneFormatter';

interface PhoneBookProps {
  // Keine Props mehr benötigt
}

export interface PhoneBookRef {
  openImportDialog: () => void;
  openAgrarmonitorSetup: () => void;
  downloadXML: () => void;
  checkPhoneNumbers: () => void;
  deleteAgrarmonitorContacts: () => void;
  deleteAllContacts: () => void;
  getContactsCount: () => number;
  getAgrarmonitorContactsCount: () => number;
  isCheckingPhoneNumbers: () => boolean;
}

const PhoneBook = forwardRef<PhoneBookRef, PhoneBookProps>((props, ref) => {
  const { user, permissions, logout } = useAuth();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [aktivFilter, setAktivFilter] = useState<string>('aktiv'); // Neuer State für Aktiv-Filter
  const [nummernFilter, setNummernFilter] = useState<string>('alle_nummern'); // Neuer State für Nummern-Filter
  const [prüfenFilter, setPrüfenFilter] = useState<string>('alle_prüfen'); // Neuer State für NummernPrüfen-Filter
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // Admin View Mode ist immer aktiviert, da es kein Toggle mehr gibt
  const adminViewMode = true;
  
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [deleteAllDialogOpen, setDeleteAllDialogOpen] = useState(false);
  const [deleteAgrarmonitorDialogOpen, setDeleteAgrarmonitorDialogOpen] = useState(false);
  const [agrarmonitorSetupOpen, setAgrarmonitorSetupOpen] = useState(false);
  const [isCheckingPhoneNumbers, setIsCheckingPhoneNumbers] = useState(false);

  // Refs für alphabetische Scrollbar
  const contactsContainerRef = useRef<HTMLDivElement>(null);
  const letterRefs = useRef<{ [letter: string]: HTMLElement | null }>({});

  useEffect(() => {
    loadContacts();
  }, [adminViewMode, aktivFilter, nummernFilter, prüfenFilter]); // Neu laden wenn Admin-Modus, Aktiv-Filter, Nummern-Filter oder Prüfen-Filter geändert wird

  useEffect(() => {
    filterContacts();
  }, [contacts, searchTerm, prüfenFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadContacts = async () => {
    try {
      // Wenn Admin und adminViewMode false ist, erzwinge Benutzer-Ansicht
      const forceUserView = isAdmin() && !adminViewMode;
      const data = await ContactService.getAllContacts(forceUserView, aktivFilter, nummernFilter);
      setContacts(data);
    } catch (error) {
      showSnackbar('Fehler beim Laden der Kontakte', 'error');
    }
  };

  const filterContacts = () => {
    let filtered = contacts;
    
    // Filter nach Suchbegriff
    if (searchTerm) {
      filtered = filtered.filter(contact => {
        const searchLower = searchTerm.toLowerCase();
        
        // Verwende displayName vom Backend falls vorhanden, sonst fallback zur alten Logik
        const primaryName = contact.displayName 
          ? contact.displayName.toLowerCase()
          : (!contact.firstname && !contact.lastname && contact.companyname) 
            ? contact.companyname.toLowerCase()
            : `${contact.firstname} ${contact.lastname}`.toLowerCase();
        
        return (
          primaryName.includes(searchLower) ||
          (contact.companyname && contact.companyname.toLowerCase().includes(searchLower)) ||
          (contact.email && contact.email.toLowerCase().includes(searchLower)) ||
          (contact.phonemobile && contact.phonemobile.includes(searchTerm)) ||
          (contact.phonehome && contact.phonehome.includes(searchTerm)) ||
          (contact.phonebusiness && contact.phonebusiness.includes(searchTerm)) ||
          (contact.phoneother && contact.phoneother.includes(searchTerm)) ||
          (contact.phonemobile2 && contact.phonemobile2.includes(searchTerm)) ||
          (contact.phonehome2 && contact.phonehome2.includes(searchTerm)) ||
          (contact.phonebusiness2 && contact.phonebusiness2.includes(searchTerm))
        );
      });
    }
    
    // Filter nach NummernPrüfen-Status
    if (prüfenFilter === 'zu_prüfen') {
      filtered = filtered.filter(contact => contact.NummernPrüfen === true);
    } else if (prüfenFilter === 'geprüft') {
      filtered = filtered.filter(contact => !contact.NummernPrüfen);
    }
    // Bei 'alle_prüfen' wird kein zusätzlicher Filter angewendet
    
    setFilteredContacts(filtered);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleAddContact = () => {
    if (!permissions.canAdd) {
      showSnackbar('Keine Berechtigung zum Hinzufügen von Kontakten', 'error');
      return;
    }
    setEditingContact(null);
    setDialogOpen(true);
  };

  const handleEditContact = (contact: Contact) => {
    // Für Agrarmonitor-Kontakte erlaube das Öffnen auch ohne Edit-Berechtigung (readonly mode)
    if (!contact.agrarmonitor && !contact.readonly && !permissions.canEdit) {
      showSnackbar('Keine Berechtigung zum Bearbeiten von Kontakten', 'error');
      return;
    }
    setEditingContact(contact);
    setDialogOpen(true);
  };

  const handleDeleteContact = async (id: string) => {
    if (!permissions.canDelete) {
      showSnackbar('Keine Berechtigung zum Löschen von Kontakten', 'error');
      return;
    }
    if (window.confirm('Möchten Sie diesen Kontakt wirklich löschen?')) {
      try {
        await ContactService.deleteContact(id);
        await loadContacts();
        showSnackbar('Kontakt erfolgreich gelöscht', 'success');
      } catch (error) {
        showSnackbar('Fehler beim Löschen des Kontakts', 'error');
      }
    }
  };

  const handleSaveContact = async (contactData: Partial<Contact>) => {
    try {
      console.log('🔄 handleSaveContact called with:', contactData);
      
      // Formatiere alle Telefonnummern nach DIN 5008 vor dem Speichern
      console.log('📞 Formatiere Telefonnummern...');
      const formattedContactData = formatContactPhoneNumbers(contactData);
      console.log('📞 Formatierte Daten:', formattedContactData);
      
      if (editingContact) {
        console.log('✏️ Aktualisiere bestehenden Kontakt:', editingContact._id);
        await ContactService.updateContact(editingContact._id, formattedContactData);
        showSnackbar('Kontakt erfolgreich aktualisiert', 'success');
      } else {
        console.log('➕ Erstelle neuen Kontakt...');
        await ContactService.createContact(formattedContactData);
        showSnackbar('Kontakt erfolgreich hinzugefügt', 'success');
      }
      
      setDialogOpen(false);
      await loadContacts();
    } catch (error) {
      console.error('❌ Fehler beim Speichern des Kontakts:', error);
      showSnackbar('Fehler beim Speichern des Kontakts', 'error');
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const isAdmin = () => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return false;
    return (user.profile.groups as string[]).includes('telefonbuch_admin');
  };

  const getUserRoleLabel = () => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return 'Betrachter';
    
    const groups = user.profile.groups as string[];
    if (groups.includes('telefonbuch_admin')) return 'Administrator';
    if (groups.includes('telefonbuch_user')) return 'Benutzer';
    if (groups.includes('telefonbuch_viewer')) return 'Betrachter';
    return 'Betrachter';
  };

  // Handler für alphabetische Scrollbar
  const handleLetterClick = (letter: string) => {
    const element = letterRefs.current[letter];
    if (element && contactsContainerRef.current) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const handleAktivFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null,
  ) => {
    if (newFilter !== null) {
      setAktivFilter(newFilter);
    }
  };

  const handleNummernFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null,
  ) => {
    if (newFilter !== null) {
      setNummernFilter(newFilter);
    }
  };

  const handlePrüfenFilterChange = (
    event: React.MouseEvent<HTMLElement>,
    newFilter: string | null,
  ) => {
    if (newFilter !== null) {
      setPrüfenFilter(newFilter);
    }
  };

  const getUserRoleColor = (): 'primary' | 'secondary' | 'error' => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return 'primary';
    
    const groups = user.profile.groups as string[];
    if (groups.includes('telefonbuch_admin')) return 'error'; // Rot für Admin
    if (groups.includes('telefonbuch_user')) return 'secondary'; // Lila für User
    if (groups.includes('telefonbuch_viewer')) return 'primary'; // Blau für Viewer
    return 'primary';
  };

  const getUserRoleIcon = () => {
    if (!user?.profile?.groups || !Array.isArray(user.profile.groups)) return null;
    
    const groups = user.profile.groups as string[];
    if (groups.includes('telefonbuch_admin')) {
      return <AdminIcon color="error" />;  // Rot für Admin
    } else if (groups.includes('telefonbuch_user')) {
      return <EditIcon color="secondary" />;  // Lila für User (Edit-Icon)
    } else if (groups.includes('telefonbuch_viewer')) {
      return <ViewIcon color="primary" />;  // Blau für Viewer
    }
    return null;
  };

  // Formatiere alle Telefonnummern in einem Kontakt
  const formatContactPhoneNumbers = (contactData: Partial<Contact>): Partial<Contact> => {
    console.log('🔧 formatContactPhoneNumbers started with:', contactData);
    
    const phoneFields = [
      'phonemobile', 'phonemobile2', 'phonehome', 'phonehome2', 
      'phonebusiness', 'phonebusiness2', 'phoneother', 'faxbusiness', 'faxhome'
    ];
    
    const formattedContact = { ...contactData };
    
    phoneFields.forEach(field => {
      const originalValue = formattedContact[field as keyof Contact] as string;
      if (originalValue) {
        console.log(`📞 Formatiere ${field}: '${originalValue}'`);
        try {
          const formattedValue = formatPhoneNumberDIN5008(originalValue);
          console.log(`📞 ${field}: '${originalValue}' -> '${formattedValue}'`);
          formattedContact[field as keyof Contact] = formattedValue as any;
        } catch (error) {
          console.error(`❌ Fehler beim Formatieren von ${field}:`, error);
          // Bei Fehler den ursprünglichen Wert beibehalten
          formattedContact[field as keyof Contact] = originalValue as any;
        }
      }
    });
    
    console.log('🔧 formatContactPhoneNumbers result:', formattedContact);
    return formattedContact;
  };

  const getPrimaryPhone = (contact: Contact): string => {
    return contact.phonemobile || contact.phonebusiness || contact.phonehome || contact.phoneother || '';
  };

  const getAllPhoneNumbers = (contact: Contact): Array<{number: string, type: string, icon: React.ReactNode}> => {
    const phones = [];
    
    // Festnetz Geschäft (1&2)
    if (contact.phonebusiness) {
      phones.push({
        number: contact.phonebusiness,
        type: 'Festnetz Geschäft',
        icon: <PhoneIcon fontSize="small" color="primary" />
      });
    }
    if (contact.phonebusiness2) {
      phones.push({
        number: contact.phonebusiness2,
        type: 'Festnetz Geschäft 2',
        icon: <PhoneIcon fontSize="small" color="primary" />
      });
    }
    
    // Festnetz Privat (1&2)
    if (contact.phonehome) {
      phones.push({
        number: contact.phonehome,
        type: 'Festnetz Privat',
        icon: <PhoneIcon fontSize="small" color="secondary" />
      });
    }
    if (contact.phonehome2) {
      phones.push({
        number: contact.phonehome2,
        type: 'Festnetz Privat 2',
        icon: <PhoneIcon fontSize="small" color="secondary" />
      });
    }
    
    // Mobilfunk (1&2)
    if (contact.phonemobile) {
      phones.push({
        number: contact.phonemobile,
        type: 'Mobil',
        icon: <MobileIcon fontSize="small" color="success" />
      });
    }
    if (contact.phonemobile2) {
      phones.push({
        number: contact.phonemobile2,
        type: 'Mobil 2',
        icon: <MobileIcon fontSize="small" color="success" />
      });
    }
    
    // Sonstige Telefonnummer
    if (contact.phoneother) {
      phones.push({
        number: contact.phoneother,
        type: 'Sonstige',
        icon: <PhoneIcon fontSize="small" color="action" />
      });
    }
    
    return phones;
  };

  const getContactInitials = (contact: Contact): string => {
    // Wenn kein Vor- und Nachname vorhanden, aber Firmenname, verwende Firmenname-Initialen
    if (!contact.firstname && !contact.lastname && contact.companyname) {
      const companyWords = contact.companyname.trim().split(' ');
      if (companyWords.length >= 2) {
        return companyWords[0].charAt(0).toUpperCase() + companyWords[1].charAt(0).toUpperCase();
      } else {
        return companyWords[0].charAt(0).toUpperCase() + (companyWords[0].charAt(1) || '').toUpperCase();
      }
    }
    
    // Standard: Vor- und Nachname-Initialen
    const firstInitial = contact.firstname ? contact.firstname.charAt(0).toUpperCase() : '';
    const lastInitial = contact.lastname ? contact.lastname.charAt(0).toUpperCase() : '';
    return firstInitial + lastInitial;
  };

  const handleDownloadXML = async () => {
    try {
      // Token vom AuthService abrufen
      const token = await authService.getAccessTokenAsync();
      if (!token) {
        showSnackbar('Keine Authentifizierung gefunden - bitte erneut anmelden', 'error');
        return;
      }

      console.log('🔍 XML Download - Token verfügbar:', !!token);

      // Verwende forceUserView Parameter wenn Admin im User-Modus ist
      const forceUserView = isAdmin() && !adminViewMode;
      const url = forceUserView ? 
        `${getApiUrl()}/contacts/contact.xml?forceUserView=true` :
        `${getApiUrl()}/contacts/contact.xml`;

      console.log('🔍 XML Download URL:', url);
      console.log('🔍 Force user view:', forceUserView);

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/xml',
        },
      });

      console.log('🔍 XML Download Response:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('XML Download Error:', errorText);
        throw new Error(`Fehler beim Download: ${response.status} ${response.statusText}`);
      }

      // Blob erstellen und Download starten
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `telefonbuch_${new Date().toISOString().split('T')[0]}.xml`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      showSnackbar('XML-Datei erfolgreich heruntergeladen', 'success');
      console.log('✅ XML Download erfolgreich');
    } catch (error) {
      console.error('❌ Fehler beim XML-Download:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler beim Download';
      showSnackbar(`Fehler beim Download: ${errorMessage}`, 'error');
    }
  };

  const handleImportComplete = () => {
    loadContacts();
    showSnackbar('Import erfolgreich abgeschlossen', 'success');
  };

  const handleDeleteAllContacts = async () => {
    try {
      const result = await ContactService.deleteAllContacts();
      showSnackbar(`${result.deletedCount} Kontakte erfolgreich gelöscht`, 'success');
      await loadContacts();
    } catch (error) {
      console.error('❌ Fehler beim Löschen aller Kontakte:', error);
      showSnackbar('Fehler beim Löschen aller Kontakte', 'error');
    }
  };

  const handleCheckAllPhoneNumbers = async () => {
    try {
      setIsCheckingPhoneNumbers(true);
      showSnackbar('Starte Telefonnummern-Prüfung...', 'success');
      
      const response = await ContactService.checkAllPhoneNumbers();
      
      showSnackbar(
        `Telefonnummern-Prüfung abgeschlossen: ${response.results.checked} Kontakte geprüft, ${response.results.updated} aktualisiert, ${response.results.validNumbers} gültige Nummern, ${response.results.invalidNumbers} ungültige Nummern`,
        'success'
      );
      
      // Kontakte neu laden um aktualisierte NummernPrüfen-Flags zu zeigen
      await loadContacts();
      
    } catch (error) {
      console.error('❌ Fehler bei der Telefonnummern-Prüfung:', error);
      showSnackbar('Fehler bei der Telefonnummern-Prüfung', 'error');
    } finally {
      setIsCheckingPhoneNumbers(false);
    }
  };

  // Imperative API für Parent-Komponente
  useImperativeHandle(ref, () => ({
    openImportDialog: () => setImportDialogOpen(true),
    openAgrarmonitorSetup: () => setAgrarmonitorSetupOpen(true),
    downloadXML: handleDownloadXML,
    checkPhoneNumbers: handleCheckAllPhoneNumbers,
    deleteAgrarmonitorContacts: () => setDeleteAgrarmonitorDialogOpen(true),
    deleteAllContacts: () => setDeleteAllDialogOpen(true),
    getContactsCount: () => contacts.length,
    getAgrarmonitorContactsCount: () => contacts.filter(c => c.agrarmonitor === true).length,
    isCheckingPhoneNumbers: () => isCheckingPhoneNumbers
  }), [contacts, isCheckingPhoneNumbers, handleDownloadXML, handleCheckAllPhoneNumbers]);

  return (
    <Box sx={{ py: 2, px: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            label="Kontakte durchsuchen..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Filter */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Aktiv-Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <FilterListIcon color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                Status:
              </Typography>
              <ToggleButtonGroup
                value={aktivFilter}
                exclusive
                onChange={handleAktivFilterChange}
                size="small"
                aria-label="Aktiv-Filter"
              >
                <ToggleButton value="aktiv" aria-label="Aktive Kontakte">
                  Aktiv
                </ToggleButton>
                <ToggleButton value="inaktiv" aria-label="Inaktive Kontakte">
                  Inaktiv
                </ToggleButton>
                <ToggleButton value="alle" aria-label="Alle Kontakte">
                  Alle
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
            
            {/* Telefonnummern-Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <ContactPhoneIcon color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                Nummern:
              </Typography>
              <ToggleButtonGroup
                value={nummernFilter}
                exclusive
                onChange={handleNummernFilterChange}
                size="small"
                aria-label="Nummern-Filter"
              >
                <ToggleButton value="mit_nummern" aria-label="Kontakte mit Telefonnummern">
                  Mit Nummern
                </ToggleButton>
                <ToggleButton value="ohne_nummern" aria-label="Kontakte ohne Telefonnummern">
                  Ohne Nummern
                </ToggleButton>
                <ToggleButton value="alle_nummern" aria-label="Alle Kontakte">
                  Alle
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Nummern-Prüfung Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <WarningIcon color="action" />
              <Typography variant="body2" color="text.secondary" sx={{ minWidth: '80px' }}>
                Prüfung:
              </Typography>
              <ToggleButtonGroup
                value={prüfenFilter}
                exclusive
                onChange={handlePrüfenFilterChange}
                size="small"
                aria-label="Nummern-Prüfung-Filter"
              >
                <ToggleButton value="zu_prüfen" aria-label="Kontakte mit zu prüfenden Nummern">
                  Zu prüfen
                </ToggleButton>
                <ToggleButton value="geprüft" aria-label="Kontakte mit geprüften Nummern">
                  Geprüft
                </ToggleButton>
                <ToggleButton value="alle_prüfen" aria-label="Alle Kontakte">
                  Alle
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          <Typography variant="h6" gutterBottom>
            {filteredContacts.length} Kontakte gefunden
          </Typography>

        <Box sx={{ position: 'relative', pr: 6 }}>
          <Box ref={contactsContainerRef} sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto', pr: 2 }}>
            <List>
              {filteredContacts.map((contact, index) => {
                // Bestimme den ersten Buchstaben für die Gruppierung
                const firstLetter = (contact.displayName || contact.lastname || contact.companyname || contact.firstname || '?').charAt(0).toUpperCase();
                const prevContact = index > 0 ? filteredContacts[index - 1] : null;
                const prevFirstLetter = prevContact ? (prevContact.displayName || prevContact.lastname || prevContact.companyname || prevContact.firstname || '?').charAt(0).toUpperCase() : null;
                const showDivider = firstLetter !== prevFirstLetter;

                return (
                  <React.Fragment key={contact._id}>
                    {showDivider && (
                      <Box
                        ref={(el) => { letterRefs.current[firstLetter] = el as HTMLElement | null; }}
                        sx={{ 
                          pt: index > 0 ? 1.5 : 0,
                          pb: 0.5,
                          pl: 2,
                          pr: 2,
                          mb: 1,
                          backgroundColor: 'primary.light',
                          borderRadius: 1,
                          borderLeft: '4px solid',
                          borderLeftColor: 'primary.main'
                        }}
                      >
                        <Typography variant="subtitle1" color="primary.contrastText" sx={{ fontWeight: 'bold', fontSize: '0.95rem' }}>
                          {firstLetter}
                        </Typography>
                      </Box>
                    )}
                    <ListItem
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  mb: 1,
                  backgroundColor: contact.aktiv === false ? '#f5f5f5' : '#fafafa',
                  opacity: contact.aktiv === false ? 0.6 : 1
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    src={contact.photourl}
                    sx={{ bgcolor: 'primary.main' }}
                  >
                    {!contact.photourl && getContactInitials(contact)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {/* Verwende displayName vom Backend oder fallback zur alten Logik */}
                      {contact.displayName ? (
                        <Typography variant="h6" component="span">
                          {contact.displayName}
                        </Typography>
                      ) : (!contact.firstname && !contact.lastname && contact.companyname) ? (
                        <Typography variant="h6" component="span">
                          {contact.companyname}
                        </Typography>
                      ) : (
                        <Typography variant="h6" component="span">
                          {contact.firstname} {contact.lastname}
                        </Typography>
                      )}
                      {contact.companyname && contact.displayName && !contact.displayName.includes(contact.companyname) && (
                        <Chip
                          icon={<BusinessIcon />}
                          label={contact.companyname}
                          size="small"
                          variant="outlined"
                        />
                      )}
                      {/* Sichtbarkeits-Indikator */}
                      <Chip
                        icon={contact.public ? <PublicIcon /> : <LockIcon />}
                        label={contact.public ? 'Öffentlich' : 'Privat'}
                        size="small"
                        color={contact.public ? 'primary' : 'default'}
                        variant="outlined"
                      />
                      {/* Aktiv-Status Indikator */}
                      {contact.aktiv === false && (
                        <Chip
                          label="Inaktiv"
                          size="small"
                          color="default"
                          variant="outlined"
                          sx={{
                            opacity: 0.7,
                            textDecoration: 'line-through'
                          }}
                        />
                      )}
                      {/* Telefonnummern-Status Indikator */}
                      {!getPrimaryPhone(contact) && (
                        <Chip
                          icon={<ContactMailIcon />}
                          label="Keine Tel.-Nr."
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{
                            opacity: 0.8
                          }}
                        />
                      )}
                      {/* Agrarmonitor-Import Kennzeichnung */}
                      {contact.agrarmonitor && (
                        <Chip
                          label={contact.readonly ? "AM 🔒" : "AM"}
                          size="small"
                          sx={{
                            backgroundColor: '#4caf50',
                            color: 'white',
                            fontWeight: 'bold',
                            minWidth: '36px',
                            fontSize: '0.75rem'
                          }}
                          variant="filled"
                          title={contact.readonly ? "Agrarmonitor-Kontakt (schreibgeschützt)" : "Agrarmonitor-Kontakt"}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 1 }}>
                      {/* Telefonnummern anzeigen */}
                      {getAllPhoneNumbers(contact).map((phone, phoneIndex) => (
                        <Box key={phoneIndex} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          {phone.icon}
                          <Typography variant="body2" title={phone.type}>
                            {phone.number}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({phone.type})
                          </Typography>
                        </Box>
                      ))}
                      
                      {/* E-Mail anzeigen */}
                      {contact.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <EmailIcon fontSize="small" color="primary" />
                          <Typography variant="body2">{contact.email}</Typography>
                        </Box>
                      )}
                      
                      {/* Besitzer-Info anzeigen, wenn es nicht der eigene Kontakt ist */}
                      {contact.owner && contact.owner !== user?.profile.sub && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccountCircle fontSize="small" color="action" />
                          <Typography variant="body2" color="text.secondary">
                            Erstellt von: {contact.ownerName || contact.ownerUsername || 'Unbekannt'}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
                <ListItemSecondaryAction>
                  {/* Edit-Button für alle Kontakte (bei Agrarmonitor-Kontakten readonly mode) */}
                  {((contact.readonly || contact.agrarmonitor) || (!contact.readonly && !contact.agrarmonitor && permissions.canEdit)) && (
                    <IconButton
                      edge="end"
                      aria-label="edit"
                      onClick={() => handleEditContact(contact)}
                      sx={{ mr: 1 }}
                      title={contact.readonly || contact.agrarmonitor ? "Details anzeigen (schreibgeschützt)" : "Bearbeiten"}
                    >
                      <EditIcon />
                    </IconButton>
                  )}
                  {/* Delete-Button nur für nicht-readonly Kontakte */}
                  {permissions.canDelete && !contact.readonly && !contact.agrarmonitor && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={() => handleDeleteContact(contact._id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
              {index < filteredContacts.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Box>
          
          <AlphabeticScrollbar 
            contacts={filteredContacts} 
            onLetterClick={handleLetterClick}
            visible={filteredContacts.length > 10}
          />
        </Box>

        {filteredContacts.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="textSecondary">
              {searchTerm ? 'Keine Kontakte gefunden.' : 'Noch keine Kontakte vorhanden.'}
            </Typography>
          </Box>
        )}
        </Paper>

        {permissions.canAdd && (
          <Fab
            color="primary"
            aria-label="add"
            onClick={handleAddContact}
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
          >
            <AddIcon />
          </Fab>
        )}

        <ContactDialog
          open={dialogOpen}
          contact={editingContact}
          isEditing={!!editingContact}
          readonly={editingContact?.readonly === true || editingContact?.agrarmonitor === true}
          onClose={handleCloseDialog}
          onSave={handleSaveContact}
        />

        <ImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          onImportComplete={handleImportComplete}
        />

        <DeleteAllContactsDialog
          open={deleteAllDialogOpen}
          onClose={() => setDeleteAllDialogOpen(false)}
          onConfirm={handleDeleteAllContacts}
          contactCount={contacts.length}
        />

        <DeleteAgrarmonitorDialog
          open={deleteAgrarmonitorDialogOpen}
          onClose={() => setDeleteAgrarmonitorDialogOpen(false)}
          onDeleteComplete={loadContacts}
        />

        <AgrarmonitorSetupDialog
          open={agrarmonitorSetupOpen}
          onClose={() => setAgrarmonitorSetupOpen(false)}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
    </Box>
  );
});

PhoneBook.displayName = 'PhoneBook';

export default PhoneBook;
